import React, { useEffect, useState } from "react";
import { useParams, Link } from "react-router-dom";
import api, { API } from "@/lib/api";
import SmartImage from "@/components/SmartImage";
import { ArrowLeft, Sparkles } from "lucide-react";

export default function CollectionDetail() {
  const { slug } = useParams();
  const [data, setData] = useState(null);
  const [notFound, setNotFound] = useState(false);

  useEffect(() => {
    setData(null); setNotFound(false);
    api.get(`/collections/${slug}`)
      .then((r) => setData(r.data))
      .catch((e) => {
        if (e?.response?.status === 404) setNotFound(true);
      });
  }, [slug]);

  if (notFound) {
    return (
      <div className="max-w-3xl mx-auto px-6 py-32 text-center">
        <h1 className="font-serif text-4xl">Collection not found.</h1>
        <Link to="/collections" className="btn-primary mt-8 inline-flex">← All collections</Link>
      </div>
    );
  }
  if (!data) return <div className="min-h-[60vh] flex items-center justify-center text-muted_ink">Loading…</div>;

  return (
    <div data-testid="collection-detail" className="max-w-6xl mx-auto px-6 md:px-12 py-12 md:py-20">
      <Link to="/collections" className="label-eyebrow text-muted_ink hover:text-terracotta inline-flex items-center gap-2">
        <ArrowLeft className="w-3 h-3" /> All collections
      </Link>

      {/* Hero */}
      <div className="mt-6 grid grid-cols-1 md:grid-cols-12 gap-8 md:gap-12 border-b border-edge pb-12">
        <div className="md:col-span-5">
          {data.cover_url && (
            <div className="aspect-[4/3] overflow-hidden bg-linen">
              <SmartImage src={data.cover_url} alt="" className="w-full h-full object-cover" />
            </div>
          )}
        </div>
        <div className="md:col-span-7 flex flex-col justify-center">
          <div className="label-eyebrow text-sage flex items-center gap-2">
            <Sparkles className="w-3.5 h-3.5" /> Curated shelf · {data.book_count} book{data.book_count === 1 ? "" : "s"}
          </div>
          <h1 className="font-serif text-5xl sm:text-6xl tracking-tight mt-3">{data.title}</h1>
          <p className="text-lg text-muted_ink mt-6 max-w-xl leading-relaxed">{data.description}</p>
        </div>
      </div>

      {/* Books */}
      <div className="mt-12 grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-6 md:gap-8">
        {data.books.map((b) => {
          const cover = b.cover_url || (b.cover_path ? `${API}/files/${b.cover_path}` : null);
          return (
            <Link
              key={b.id}
              to={`/book/${b.id}`}
              data-testid={`collection-book-${b.id}`}
              className="group block"
            >
              <div className="aspect-[3/4] overflow-hidden bg-linen book-card-cover">
                {cover && <SmartImage src={cover} alt={b.title} className="w-full h-full object-cover transition-transform duration-500 group-hover:scale-[1.03]" />}
              </div>
              <div className="mt-3">
                <div className="label-eyebrow text-sage text-[10px]">{b.category}</div>
                <div className="font-serif text-lg mt-1 leading-tight group-hover:text-terracotta transition-colors">{b.title}</div>
                <div className="text-sm italic text-muted_ink">by {b.author}</div>
                <div className="text-sm mt-1">${Number(b.price).toFixed(2)}</div>
              </div>
            </Link>
          );
        })}
      </div>
    </div>
  );
}
